import { useState } from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useAgentKnowledgeBases, useRemoveAgentKnowledgeBase } from '../../hooks/useKnowledge';
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableRow,
  TableHeader,
} from '../ui/DataTable';

/**
 * The knowledge bases an agent always searches (tasks 573/574).
 *
 * Mirrors `AgentMcpConnectionsList` — same junction shape, same two-step remove
 * confirmation — because to a User these are the same kind of decision: what
 * this agent is allowed to reach. An agent with several attached bases searches
 * all of them; detaching removes the attachment only.
 */
interface AgentKnowledgeBasesListProps {
  agentId: string;
  onAddKnowledgeBase: () => void;
}

export function AgentKnowledgeBasesList({
  agentId,
  onAddKnowledgeBase,
}: AgentKnowledgeBasesListProps) {
  const { data: attachments, isLoading, error } = useAgentKnowledgeBases(agentId);
  const removeAttachment = useRemoveAgentKnowledgeBase();
  const [removingId, setRemovingId] = useState<string | null>(null);

  const handleRemove = async (knowledgeBaseId: string) => {
    if (removingId === knowledgeBaseId) {
      try {
        await removeAttachment.mutateAsync({ agentId, knowledgeBaseId });
        setRemovingId(null);
      } catch (removeError) {
        console.error('Failed to detach the knowledge base:', removeError);
      }
    } else {
      setRemovingId(knowledgeBaseId);
    }
  };

  const header = (
    <>
      <h3 className="text-sm font-medium text-gray-900 dark:text-white">Knowledge Bases</h3>
      <button
        type="button"
        onClick={onAddKnowledgeBase}
        data-testid="agent-add-knowledge-base"
        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300"
      >
        <PlusIcon className="h-4 w-4 mr-1" />
        Attach Knowledge Base
      </button>
    </>
  );

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-gray-900 dark:text-white">Knowledge Bases</h3>
        </div>
        <div className="animate-pulse space-y-2">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded" />
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-red-200 dark:border-red-800 p-6">
        <p className="text-sm text-red-600 dark:text-red-400">
          Error loading knowledge bases. Please try again.
        </p>
      </div>
    );
  }

  if (!attachments || attachments.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between mb-4">{header}</div>
        <div className="text-center py-8">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No knowledge bases attached. This agent has no standing material to draw on.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        {header}
      </div>

      <DataTable>
        <DataTableHead>
          <TableHeader>Knowledge base</TableHeader>
          <TableHeader>Description</TableHeader>
          <TableHeader className="w-24">Actions</TableHeader>
        </DataTableHead>
        <DataTableBody>
          {attachments.map((attachment) => (
            <DataTableRow key={attachment.id}>
              <DataTableCell>
                <div className="text-sm font-medium text-gray-900 dark:text-white">
                  {attachment.knowledgeBase?.name ?? 'Unknown'}
                </div>
              </DataTableCell>
              <DataTableCell>
                <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
                  {attachment.knowledgeBase?.description || '-'}
                </div>
              </DataTableCell>
              <DataTableCell>
                <div className="flex items-center space-x-2">
                  {removingId === attachment.knowledgeBaseId ? (
                    <div className="flex space-x-1">
                      <button
                        type="button"
                        onClick={() => handleRemove(attachment.knowledgeBaseId)}
                        disabled={removeAttachment.isPending}
                        className="text-xs text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 disabled:opacity-50"
                      >
                        {removeAttachment.isPending ? 'Removing...' : 'Confirm'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setRemovingId(null)}
                        className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleRemove(attachment.knowledgeBaseId)}
                      aria-label={`Detach ${attachment.knowledgeBase?.name ?? 'knowledge base'}`}
                      title="Detach — the knowledge base and its documents are kept"
                      className="text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </DataTableCell>
            </DataTableRow>
          ))}
        </DataTableBody>
      </DataTable>
    </div>
  );
}

export default AgentKnowledgeBasesList;
